import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { CountBasis, TimeframeKey } from "../data/types";

/**
 * Dashboard v2 filter state.
 *
 * Lives in Redux (not React Context) so it SURVIVES ROUTING: the
 * `DashboardV2Provider` unmounts when you navigate away from the dashboard,
 * which used to drop every filter/selection. The store outlives the route, so
 * timeframe / branch / role / user / country / basis / compare are all preserved
 * when you come back. In-memory only (not redux-persist) — a full page reload
 * still starts clean, which is the expected behaviour for dashboard filters.
 */
export interface DashboardV2State {
  timeframe: TimeframeKey;
  branch: string;
  role: string;
  user: string;
  /** Country CF option id (study-abroad / industry 1); "all" = no filter. */
  country: string;
  /**
   * Selected intakes (study-abroad / industry 1) as `"<year>-<month>"` keys with
   * month 0-indexed (the same key the Intake selector uses). Empty = no filter
   * (all intakes). Scopes EVERY widget except the Intake-insights widget.
   */
  intakes: string[];
  /**
   * Global custom-field filter pills (CustomInputs with `isGlobalFilter`):
   * `{ [customInputId]: optionId }`. Absent key = no filter for that field.
   * Serialized to `cf_<id>=<optionId>` params — the same params the Leads
   * page sends, honored dashboard-wide server-side.
   */
  globalCf: Record<string, string>;
  customStart: string | null;
  customEnd: string | null;
  basis: CountBasis;
  compare: boolean;
  /**
   * Recent Activity feed filters. They live here rather than in the page's own
   * `useState` for the same reason as everything above: so drilling from the
   * widget into /activity and back doesn't reset what you'd narrowed to.
   * `"all"` = every activity type.
   */
  activityType: string;
  activitySearch: string;
  /**
   * THE follow-up slot role, shared by all three follow-up widgets (rail,
   * by-user, by-branch). One control, one value: the rail owns the visible
   * pill and the two load widgets follow it (user directive 2026-09-01 — "I
   * don't need the same role selector" three times on one dashboard).
   *
   * `undefined` = untouched, so each widget falls back to the same seeded
   * default (`defaultPersonalRoleId`). Never `"all"` — the option was removed.
   *
   * MULTI-SELECT since 2026-09-09: this is ONE role id or a COMMA LIST
   * ("143,331"), passed straight through as `?followUpRoleId=`. The server
   * reads several picks as the UNION of what each means on its own (a slot
   * role's own book, a managerial role's team within its own fence), so the
   * ids stay in the pill's option order and are never empty.
   */
  followUpRole?: string;
  /**
   * PER-WIDGET follow-up role, keyed by widget ("byUser" / "byBranch").
   *
   * The three follow-up widgets used to share `followUpRole` ("one selector,
   * shown in three places"). User directive 2026-09-09: *"they are individual
   * widgets"* — filtering one must not re-narrow, and re-load, the other two.
   * The rail keeps `followUpRole`; each board keeps its own entry here.
   * `undefined` = untouched → that widget falls back to the seeded default.
   */
  followUpRoleByWidget: Record<string, string | undefined>;
  /**
   * The header box on the dashboard: narrows the widget grid to the cards
   * whose title holds every typed word ("team" → My team, Lead distribution
   * per counsellor…). Cleared when the box leaves the dashboard.
   */
  widgetSearch: string;
}

const initialState: DashboardV2State = {
  timeframe: "all",
  branch: "all",
  role: "all",
  user: "all",
  country: "all",
  intakes: [],
  globalCf: {},
  customStart: null,
  customEnd: null,
  basis: "leads",
  compare: false,
  activityType: "all",
  activitySearch: "",
  followUpRole: undefined,
  followUpRoleByWidget: {},
  widgetSearch: "",
};

const dashboardV2Slice = createSlice({
  name: "dashboardV2",
  initialState,
  reducers: {
    setTimeframe: (s, a: PayloadAction<TimeframeKey>) => {
      s.timeframe = a.payload;
    },
    setBranch: (s, a: PayloadAction<string>) => {
      s.branch = a.payload;
    },
    setFollowUpRole: (s, a: PayloadAction<string | undefined>) => {
      s.followUpRole = a.payload;
    },
    setFollowUpRoleForWidget: (
      s,
      a: PayloadAction<{ widget: string; role: string | undefined }>,
    ) => {
      s.followUpRoleByWidget = {
        ...s.followUpRoleByWidget,
        [a.payload.widget]: a.payload.role,
      };
    },
    setRole: (s, a: PayloadAction<string>) => {
      s.role = a.payload;
    },
    setUser: (s, a: PayloadAction<string>) => {
      s.user = a.payload;
    },
    setCountry: (s, a: PayloadAction<string>) => {
      s.country = a.payload;
    },
    setIntakes: (s, a: PayloadAction<string[]>) => {
      s.intakes = a.payload;
    },
    /** Set one global CF pill; null/"all" clears that field's filter. */
    setGlobalCf: (
      s,
      a: PayloadAction<{ fieldId: string; optionId: string | null }>,
    ) => {
      const { fieldId, optionId } = a.payload;
      if (!optionId || optionId === "all") delete s.globalCf[fieldId];
      else s.globalCf[fieldId] = optionId;
    },
    setCustomRange: (
      s,
      a: PayloadAction<{ start: string | null; end: string | null }>,
    ) => {
      s.customStart = a.payload.start;
      s.customEnd = a.payload.end;
    },
    setBasis: (s, a: PayloadAction<CountBasis>) => {
      s.basis = a.payload;
    },
    setCompare: (s, a: PayloadAction<boolean>) => {
      s.compare = a.payload;
    },
    toggleCompare: (s) => {
      s.compare = !s.compare;
    },
    setActivityType: (s, a: PayloadAction<string>) => {
      s.activityType = a.payload;
    },
    setActivitySearch: (s, a: PayloadAction<string>) => {
      s.activitySearch = a.payload;
    },
    setWidgetSearch: (s, a: PayloadAction<string>) => {
      s.widgetSearch = a.payload;
    },
    /** "Reset all filters" — clears the filter fields. */
    reset: (s) => {
      s.timeframe = "all";
      s.branch = "all";
      s.role = "all";
      s.user = "all";
      s.country = "all";
      s.intakes = [];
      s.globalCf = {};
      s.customStart = null;
      s.customEnd = null;
      s.basis = "leads";
      s.compare = false;
    },
  },
});

export const dashboardV2Actions = dashboardV2Slice.actions;
export default dashboardV2Slice.reducer;
